/** User files surface (`/files`). All routes require Bearer auth. */

import { BackendClient } from './BackendClient';

export interface FilesQuery {
  purpose?: string | string[];
  include_temporary?: boolean;
  include_deleted?: boolean;
  is_public?: boolean | null;
  limit?: number;
  offset?: number;
}

export interface UploadFilesOptions {
  purpose?: string;
  is_public?: boolean;
  is_temporary?: boolean;
  job_id?: string;
}

export interface FileMetadataUpdate {
  is_public?: boolean;
  is_temporary?: boolean;
  purpose?: string;
}

export class FilesClient extends BackendClient {
  constructor(apiKey?: string, webBackendUrl?: string) {
    super(apiKey, webBackendUrl);
    this.addEndpoint('files', 'GET', 'required');
    this.addEndpoint('files/usage', 'GET', 'required');
  }

  async getStorageUsage(): Promise<Record<string, any>> {
    return this.getJson('files/usage');
  }

  async listFiles(options: FilesQuery = {}): Promise<any[]> {
    const purpose = options.purpose ?? 'USER_UPLOAD';
    const raw = await this.getJson('files', {
      purpose: Array.isArray(purpose) ? purpose : [purpose],
      limit: options.limit ?? 25,
      offset: options.offset ?? 0,
      include_temporary: options.include_temporary,
      include_deleted: options.include_deleted,
      is_public: options.is_public,
    });
    return Array.isArray(raw) ? raw : [];
  }

  /** Resolve one file by file id, job id, or storage URL. */
  async getFile(selector: string | number): Promise<any> {
    return this.getJson(`files/${encodeURIComponent(String(selector))}`);
  }

  async updateFile(fileId: string | number, update: FileMetadataUpdate): Promise<any> {
    return this.putJson(`files/${fileId}`, undefined, update);
  }

  async deleteFile(fileId: string | number): Promise<boolean> {
    const result = await this.deleteJson(`files/${fileId}`);
    return result === true || result == null;
  }

  async uploadFiles(
    files: Blob | File | Array<Blob | File>,
    options: UploadFilesOptions = {},
  ): Promise<any[]> {
    const fileList = Array.isArray(files) ? files : [files].filter(Boolean);
    const formData = new FormData();
    for (const file of fileList) formData.append('files', file);

    const raw = await this.postForm('files', formData, {
      purpose: options.purpose || 'USER_UPLOAD',
      is_public: String(options.is_public ?? false),
      is_temporary: String(options.is_temporary ?? false),
      job_id: options.job_id ?? '',
    });
    return Array.isArray(raw) ? raw : [];
  }
}
